import { useCallback, useEffect, useMemo, useState } from "react";
import api, { CATEGORIES, STATUSES, IMPACTS, US_STATES, formatLabel } from "@/lib/api";
import { useCoverage } from "@/hooks/usePublicConfig";
import EntryCard from "@/components/EntryCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, X, Bookmark, Share2, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams, useParams } from "react-router-dom";

const ALL = "__all__";

export default function Tracker() {
  const { levels: JURISDICTION_LEVELS, countries: COUNTRIES } = useCoverage();
  const [params, setParams] = useSearchParams();
  const { slug: routeCategory } = useParams();

  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    category: routeCategory || params.get("category") || "",
    status: params.get("status") || "",
    impact: params.get("impact") || "",
    jurisdiction_level: params.get("jurisdiction_level") || "",
    jurisdiction: params.get("jurisdiction") || "",
    sort: params.get("sort") || "date_desc",
  });

  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [saveLink, setSaveLink] = useState("");
  const [saving, setSaving] = useState(false);

  const PAGE_SIZE = 24;

  const fetchPage = useCallback(async (targetPage) => {
    if (targetPage === 1) setLoading(true); else setLoadingMore(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await api.get("/entries", { params: { ...cleaned, page: targetPage, page_size: PAGE_SIZE } });
      const { items, total: t, total_pages } = res.data;
      setTotal(t);
      setTotalPages(total_pages);
      setPage(targetPage);
      setEntries((prev) => targetPage === 1 ? items : [...prev, ...items]);
    } catch (e) {
      console.error("Failed to load entries", e);
      toast.error("Failed to load entries");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  useEffect(() => { fetchPage(1); }, [fetchPage]);

  // Sync to URL
  useEffect(() => {
    const sp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && sp.set(k, v));
    setParams(sp, { replace: true });
  }, [filters, setParams]);

  const update = (key, value) => setFilters((f) => ({ ...f, [key]: value === ALL ? "" : value }));
  const clearAll = () => setFilters({ q: "", category: "", status: "", impact: "", jurisdiction_level: "", jurisdiction: "", sort: "date_desc" });

  const activeFilterCount = useMemo(
    () => Object.entries(filters).filter(([k, v]) => v && k !== "sort").length,
    [filters]
  );

  // Build a query-string suffix of filters compatible with /api/export/entries
  const exportFilterQS = useMemo(() => {
    const sp = new URLSearchParams();
    ["category", "impact", "jurisdiction", "jurisdiction_level"].forEach((k) => {
      if (filters[k]) sp.set(k, filters[k]);
    });
    const s = sp.toString();
    return s ? `&${s}` : "";
  }, [filters]);

  const onSaveSearch = () => {
    setSaveLabel(`Search · ${new Date().toLocaleDateString()}`);
    setSaveLink("");
    setSaveOpen(true);
  };

  const submitSave = async () => {
    if (!saveLabel.trim()) return;
    setSaving(true);
    try {
      const res = await api.post("/saved-searches", { label: saveLabel.trim(), filters });
      const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
      const link = `${PUBLIC_URL}/share/${res.data.share_token}`;
      setSaveLink(link);
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Saved! Share link copied to clipboard.");
      } catch {
        toast.success("Saved! Copy the link below.");
      }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(saveLink); toast.success("Copied"); } catch (err) { console.error("Copy link failed", err); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="border-b border-black/15 pb-6 mb-8">
        <div className="overline">/the database</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2">
          The Tracker
        </h1>
        <p className="text-base text-[#333] mt-3 max-w-2xl">
          Every entry below is a documented government action affecting women's rights, with citations
          attached. Filter, search, and bookmark your view.
        </p>
      </div>

      {/* Filter strip */}
      <div className="bg-white border border-black/15 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-b border-black/10">
          <div className="lg:col-span-5 border-b lg:border-b-0 lg:border-r border-black/10 p-4 flex items-center gap-2">
            <Search className="w-4 h-4 text-[#555]" />
            <Input
              data-testid="filter-search-input"
              placeholder="Search title, summary, tag, or actor…"
              value={filters.q}
              onChange={(e) => update("q", e.target.value)}
              className="border-none shadow-none focus-visible:ring-0 px-0 h-9"
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            <FilterSelect testid="filter-category" label="Category" value={filters.category} onChange={(v) => update("category", v)} options={CATEGORIES.map(c => ({ value: c.slug, label: c.label }))} />
            <FilterSelect testid="filter-impact" label="Impact" value={filters.impact} onChange={(v) => update("impact", v)} options={IMPACTS.map(s => ({ value: s, label: formatLabel(s) }))} />
            <FilterSelect testid="filter-status" label="Status" value={filters.status} onChange={(v) => update("status", v)} options={STATUSES.map(s => ({ value: s, label: formatLabel(s) }))} />
            <FilterSelect testid="filter-level" label="Level" value={filters.jurisdiction_level} onChange={(v) => update("jurisdiction_level", v)} options={JURISDICTION_LEVELS.map(s => ({ value: s, label: formatLabel(s) }))} />
            <FilterSelect
              testid="filter-state"
              label={filters.jurisdiction_level === "international" ? "Country" : "Jurisdiction"}
              value={filters.jurisdiction}
              onChange={(v) => update("jurisdiction", v)}
              options={(filters.jurisdiction_level === "international" ? COUNTRIES : US_STATES).map(s => ({ value: s, label: s }))}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between p-3 gap-2 bg-[#F9F9F9]">
          <div className="text-xs font-mono-plex tracking-wider uppercase text-[#555]">
            {loading ? "Loading…" : `${entries.length} ${entries.length === 1 ? "entry" : "entries"} · ${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} active`}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filters.sort} onValueChange={(v) => update("sort", v)}>
              <SelectTrigger data-testid="sort-select" aria-label="Sort entries" className="rounded-none w-[180px] h-9 border-black/30 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="date_desc">Newest first</SelectItem>
                <SelectItem value="date_asc">Oldest first</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="save-search-btn" variant="outline" className="rounded-none h-9 border-black" onClick={onSaveSearch}>
              <Bookmark className="w-4 h-4 mr-2" /> Save & share
            </Button>
            <a
              href={`${window.location.origin}/api/export/entries?fmt=csv${exportFilterQS}`}
              data-testid="export-csv-btn"
              className="inline-flex items-center justify-center h-9 px-3 border border-black bg-white hover:bg-black hover:text-white font-mono-plex text-sm uppercase tracking-wider"
            >
              <Download className="w-4 h-4 mr-2" /> CSV
            </a>
            <a
              href={`${window.location.origin}/api/export/entries?fmt=json${exportFilterQS}`}
              data-testid="export-json-btn"
              className="inline-flex items-center justify-center h-9 px-3 border border-black bg-white hover:bg-black hover:text-white font-mono-plex text-sm uppercase tracking-wider"
            >
              JSON
            </a>
            <Button data-testid="clear-filters-btn" variant="outline" className="rounded-none h-9 border-black/40" onClick={clearAll} disabled={activeFilterCount === 0 && !filters.q}>
              <X className="w-4 h-4 mr-2" /> Clear
            </Button>
          </div>
        </div>
      </div>

      {(() => {
        if (loading) {
          return <div className="text-center py-16 font-mono-plex text-[#555] uppercase tracking-wider text-sm">Loading entries…</div>;
        }
        if (entries.length === 0) {
          return (
            <div className="text-center py-16 border border-dashed border-black/20 bg-white">
              <p className="font-display text-3xl font-bold">No matching entries.</p>
              <p className="text-[#555] mt-2">Try removing a filter or broadening your search.</p>
              <Button onClick={clearAll} className="mt-6 rounded-none bg-black text-white" data-testid="empty-clear-btn">Reset filters</Button>
            </div>
          );
        }
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {entries.map((e) => <EntryCard key={e.id} entry={e} />)}
            </div>
            {page < totalPages && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={() => fetchPage(page + 1)}
                  disabled={loadingMore}
                  variant="outline"
                  className="rounded-none border-black h-11 px-8"
                  data-testid="load-more-btn"
                >
                  {loadingMore ? "Loading…" : `Load more · ${total - entries.length} remaining`}
                </Button>
              </div>
            )}
          </>
        );
      })()}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-none max-w-md border-2 border-black p-0 gap-0 bg-white">
          <DialogHeader className="px-6 py-4 border-b border-black/15">
            <DialogTitle className="font-display text-2xl font-bold tracking-tight" data-testid="save-search-title">
              Save & share this view
            </DialogTitle>
            <DialogDescription className="text-xs font-mono-plex uppercase tracking-wider text-[#555]">
              Anyone with the link sees the same filters applied.
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-3">
            <label className="block">
              <span className="overline mb-1.5 block">Name this view</span>
              <Input
                data-testid="save-search-input"
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value)}
                className="rounded-none border-black/30 h-11"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && !saveLink) { e.preventDefault(); submitSave(); } }}
              />
            </label>
            {saveLink && (
              <div className="border border-black/15 bg-[#F9F9F9] p-3" data-testid="save-search-link">
                <div className="overline mb-1">Shareable link</div>
                <div className="flex items-center gap-2">
                  <code className="font-mono-plex text-[12px] truncate flex-1 text-[#222]">{saveLink}</code>
                  <Button onClick={copyLink} type="button" variant="outline" className="rounded-none border-black h-8 px-2" data-testid="save-search-copy">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-black/15 bg-white gap-2">
            <Button variant="outline" onClick={() => setSaveOpen(false)} className="rounded-none border-black/40 h-10">
              {saveLink ? "Done" : "Cancel"}
            </Button>
            {!saveLink && (
              <Button onClick={submitSave} disabled={saving || !saveLabel.trim()} className="rounded-none bg-black text-white h-10" data-testid="save-search-submit">
                {saving ? "Saving…" : "Save & copy link"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="border-r last:border-r-0 border-black/10 p-2">
      <div className="overline text-[11px] px-1" id={`${testid}-label`}>{label}</div>
      <Select value={value || ALL} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testid}
          aria-label={`Filter by ${label}`}
          aria-labelledby={`${testid}-label`}
          className="rounded-none border-none shadow-none focus:ring-0 h-9 px-1 text-sm"
        >
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent className="rounded-none max-h-72">
          <SelectItem value={ALL}>All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
